import React, { useEffect, useState } from 'react';
import {
  Box,
  Tooltip,
  Typography,
  IconButton,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { setBlinkByWorkflow } from '../state/slices/mappingSlice';
import ScheduleIcon from '../workflows/images/calendar.png';
import ExecuteIcon from '../workflows/images/play_button.png';
import SaveIcon from '../workflows/images/floppy-disk.png';
import EditIcon from '../workflows/images/pencilicon.png';
import { updateWorkflowName } from '../apis/Workflows';
import { renameWorkflow } from '../state/slices/workflowSlice';
import { useThemeContext } from '../Context/ThemeContext';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

const MotionBox = motion(Box);

const WorkflowActionBar = ({ workflowId, onSave, onRunNow, onScheduleOpen, enableBacktrack, setEnableBacktrack, showExecutionSection, onToggleExecutionSection }) => {
  const { darkMode } = useThemeContext();
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [nameVal, setNameVal] = useState('');

  const theme = useTheme();
  // const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const allWorkflows = useSelector((s) => s.workflows?.list || []);
  const wf = useSelector((s) =>
    s.workflows?.list?.find((w) => w.workflow_id === workflowId),
  );

  const workflowStoreEntry = useSelector(
    (state) => state.workflows?.workflows?.[workflowId],
  );
  const hasUnsavedChanges = useSelector(
    (state) => state.workflows?.unsavedChangesByWorkflow?.[workflowId],
  );
  const workflowConfigurationId =
    workflowStoreEntry?.workflowConfigurationId ?? null;
  
  // Check if mappings exist (same logic as handleWorkflowSave)
  const mappingConnectionDetails = useSelector(
    (state) =>
      state.mapping.mappingsByWorkflow?.[workflowId]?.connectionDetails,
  );
  const workflowConnectionDetails = workflowStoreEntry?.connectionDetails;
  const connectionDetails =
    mappingConnectionDetails && mappingConnectionDetails.length > 0
      ? mappingConnectionDetails
      : (workflowConnectionDetails ?? []);
  
  // Disable Save button if no mappings exist (same logic as etl-frontend handleWorkflowSave)
  const hasNoMappings = !connectionDetails || connectionDetails.length === 0;
  
  const disableActions =
    Boolean(hasUnsavedChanges) || !workflowConfigurationId;
  const scheduleButtonTooltip = disableActions
    ? workflowConfigurationId
      ? 'Save your latest changes before scheduling.'
      : 'Save the workflow before scheduling.'
    : undefined;
  const executeButtonTooltip = disableActions
    ? workflowConfigurationId
      ? 'Save your latest changes before executing.'
      : 'Save the workflow before executing.'
    : undefined;

  useEffect(() => {
    setNameVal(wf?.name || '');
  }, [wf?.name]);

  const IconCircleButton = ({
    title,
    imgSrc,
    onClick,
    size = 40,
    imgSize = 26,
    disabled = false,
    tooltip,
    variant = 'circle',
  }) => {
    const tooltipTitle = tooltip ?? title;
    const isPlain = variant === 'plain';
    return (
      <Tooltip title={tooltipTitle} arrow>
        <MotionBox
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={title}
        aria-disabled={disabled}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          onClick?.(e);
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.(e);
          }
        }}
        whileHover={disabled ? undefined : { scale: 1.06 }}
        whileTap={disabled ? undefined : { scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 380, damping: 22 }}
        sx={{
          backgroundColor: isPlain ? 'transparent' : '#fff',
          width: isPlain ? 'auto' : `${size}px`,
          height: isPlain ? 'auto' : `${size}px`,
          borderRadius: isPlain ? 0 : '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          border: isPlain ? 'none' : '1px solid #E6E6E6',
          boxShadow: isPlain ? 'none' : '0px 0px 4px 0px #0000000D',
          '&:hover': isPlain
            ? {}
            : {
                backgroundColor: '#E5E9FF',
                boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
              },
          '&:active': isPlain
            ? {}
            : {
                boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
              },
          '&:focus-visible': isPlain
            ? { outline: 'none' }
            : {
                outline: 'none',
                boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
              },
        }}
      >
        <Box
          component="img"
          src={imgSrc}
          alt={title}
          sx={{
            width: isPlain ? `${imgSize}px` : `${Math.max(imgSize - 8, 14)}px`,
            height: isPlain ? `${imgSize}px` : `${Math.max(imgSize - 8, 14)}px`,
            backgroundColor: isPlain ? 'transparent' : '#fff',
            pointerEvents: 'none',
          }}
        />
        </MotionBox>
      </Tooltip>
    );
  };

  IconCircleButton.propTypes = {
    title: PropTypes.string.isRequired,
    imgSrc: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    size: PropTypes.number,
    imgSize: PropTypes.number,
    disabled: PropTypes.bool,
    tooltip: PropTypes.string,
    variant: PropTypes.oneOf(['circle', 'plain']),
  };

  const handleClick = () => {
    dispatch(setBlinkByWorkflow({ workflowId, button: 'save', value: false }));
    dispatch(setBlinkByWorkflow({ workflowId, button: 'execute', value: true }));
    onSave();
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleSaveName = async () => {
    const newName = nameVal.trim();
    if (!newName || !workflowId) return;

    const isDuplicate = allWorkflows.some(
      (w) => w.workflow_id !== workflowId && w.name === newName,
    );
    if (isDuplicate) {
      toast.error('Workflow with this name already exists.', { autoClose: 3000 });
      return;
    }

    if (wf?.name === newName) {
      setEditing(false);
      return;
    }

    try {
      await updateWorkflowName(workflowId, newName);
      dispatch(renameWorkflow({ workflowId, name: newName }));
      toast.success('Workflow renamed successfully', { autoClose: 2000 });
      setEditing(false);
    } catch (e) {
      console.error('Rename failed', e);
    }
  };

  // Responsive icon/button sizes
  const circleSize = isMdDown ? 28 : 32;
  const scheduleImg = isMdDown ? 18 : 20;
  const executeImg = isMdDown ? 22 : 24;
  const editImg = 15;

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: { xs: 44, sm: 48 },
          bgcolor: '#0F172A',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'fixed',
          top: { xs: 40, sm: 40 },
          zIndex: 1202,
          boxShadow: '0px 0px 10px 0px #0000000D',
        }}
      >
        <Box
          sx={{
            bgcolor: 'transparent',
            display: 'flex',
            flexWrap: 'wrap',               
            justifyContent: 'space-between',
            width: '100%',
            mx: { xs: 1.5, sm: 2, md: 4 },   
            gap: { xs: 0.5, sm: 0.75, md: 1 }, 
            rowGap: { xs: 0.5, sm: 0.5 },      
          }}
        >
          {/* Left: Back + Name/Rename */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              flex: '1 1 220px',                // lets it shrink/grow and wrap
              gap: { xs: 0.25, sm: 0.5 },
              order: 1,
            }}
          >
            <Tooltip title="Go back">
              <IconButton
                onClick={handleBack}
                sx={{
                  color: '#fff',
                  p: { xs: '2px', sm: '4px' },
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                <ArrowBackIosIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
              </IconButton>
            </Tooltip>

            {editing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
                <TextField
                  value={nameVal}
                  onChange={(e) => setNameVal(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') {
                      setNameVal(wf?.name || '');
                      setEditing(false);
                    }
                  }}
                  variant="standard"
                  size="small"
                  autoFocus
                  inputProps={{ maxLength: 80 }}
                  sx={{
                    minWidth: { xs: 120, sm: 140 },
                    maxWidth: { xs: 160, sm: 220, md: 280 },
                    color: darkMode ? '#fff' : 'inherit',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleSaveName}
                  disabled={!nameVal.trim() || nameVal.trim() === wf?.name}
                >
                  <Check fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    setNameVal(wf?.name || '');
                    setEditing(false);
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.5, sm: 1 },
                  minWidth: 0,
                }}
              >
                <Tooltip title={workflowId}>
                  <motion.div
                    initial={{ y: -6, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.28, ease: 'easeOut' }}
                  >
                <Typography
                  noWrap
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    color: darkMode ? '#fff' : 'inherit',
                    maxWidth: { xs: 110, sm: 160, md: 220 },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                        px: 1.2,
                      }}
                    >
                      {wf?.name ?? ''}
                    </Typography>
                  </motion.div>
                </Tooltip>
                <IconCircleButton
                  title="Rename"
                  imgSrc={EditIcon}
                  onClick={() => setEditing(true)}
                  size={circleSize}
                  imgSize={editImg}
                  variant="plain"
                />
              </Box>
            )}
          </Box>

          {/* Right: Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: { xs: 1.25, sm: 2.5, md: 4 },
              flex: '1 1 260px',
              width: '100%',
              order: { xs: 2, sm: 3 },
              minWidth: 0,
            }}
          >
            {setEnableBacktrack && enableBacktrack && (
              <Tooltip title="Bidirectional is enabled (automatically enabled when Bidirectional node is added)">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mr: { xs: 0.5, sm: 1 },
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                    border: '1px solid',
                    borderColor: darkMode ? 'rgba(156, 39, 176, 0.3)' : 'rgba(156, 39, 176, 0.2)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      color: darkMode ? '#ce93d8' : '#9c27b6',
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                    }}
                  >
                    ✓ Bidirectional
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {onToggleExecutionSection && (
              <Tooltip title={showExecutionSection ? 'Hide Execution Section' : 'Show Execution Section'} arrow>
                <MotionBox
                  role="button"
                  tabIndex={0}
                  aria-label={showExecutionSection ? 'Hide Execution Section' : 'Show Execution Section'}
                  onClick={onToggleExecutionSection}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggleExecutionSection();
                    }
                  }}
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                  sx={{
                    backgroundColor: '#fff',
                    width: `${circleSize}px`,
                    height: `${circleSize}px`,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    border: '1px solid #E6E6E6',
                    boxShadow: '0px 0px 4px 0px #0000000D',
                    '&:hover': {
                      backgroundColor: '#E5E9FF',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                    },
                    '&:active': {
                      boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                    },
                    '&:focus-visible': {
                      outline: 'none',
                      boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
                    },
                  }}
                >
                  {showExecutionSection ? (
                    <VisibilityIcon 
                      sx={{ 
                        fontSize: `${Math.max(scheduleImg - 8, 14)}px`,
                        color: '#1e293b',
                      }} 
                    />
                  ) : (
                    <VisibilityOffIcon 
                      sx={{ 
                        fontSize: `${Math.max(scheduleImg - 8, 14)}px`,
                        color: '#1e293b',
                      }} 
                    />
                  )}
                </MotionBox>
              </Tooltip>
            )}
            <IconCircleButton
              title="Schedule"
              imgSrc={ScheduleIcon}
              onClick={onScheduleOpen}
              size={circleSize}
              imgSize={scheduleImg}
              disabled={disableActions}
              tooltip={scheduleButtonTooltip}
            />
            <IconCircleButton
              title="Execute"
              imgSrc={ExecuteIcon}
            onClick={onRunNow}
            size={circleSize}
            imgSize={executeImg}
            disabled={disableActions}
            tooltip={executeButtonTooltip}
          />
            <IconCircleButton
              title="Save"
              imgSrc={SaveIcon}
              onClick={handleClick}
              size={circleSize}
              imgSize={executeImg}
              disabled={hasNoMappings}
              tooltip={hasNoMappings ? 'No mapping found! Please configure mapping before saving.' : 'Save'}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

WorkflowActionBar.propTypes = {
  workflowId: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onRunNow: PropTypes.func.isRequired,
  onScheduleOpen: PropTypes.func.isRequired,
  enableBacktrack: PropTypes.bool,
  setEnableBacktrack: PropTypes.func,
  showExecutionSection: PropTypes.bool,
  onToggleExecutionSection: PropTypes.func,
};

export default WorkflowActionBar;
